import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import NavigationIcon from 'atoms/NavigationIcon';
import IcomoonIcon from 'atoms/IcomoonIcon';
import SearchBox from 'atoms/SearchBox';
import DashboardLoyaltyTabs from 'molecules/DashboardLoyaltyTabs';
import {
  MOBILE_PRIMARY_NAV_COUNT,
} from 'hooks/useNavigationMenuItems';
import { NAV_ICON_KEYS } from 'utils/navigationIcons';
import { useNavigationMenu } from 'context/NavigationMenuContext';

function shortenLabel(name) {
  if (!name) return '';
  if (name.length <= 10) return name;
  const words = name.split(' ');
  if (words.length > 1 && words[0].length <= 9) return words[0];
  return `${name.slice(0, 9)}…`;
}

function NavItem({ item, onNavigate }) {
  const isActive = item.active;
  const isDisabled = item.disabled;

  const content = (
    <>
      <span
        className={`flex items-center justify-center w-9 h-9 rounded-xl transition-colors duration-200 ${
          isActive ? 'bg-primary-900/10' : ''
        }`}
      >
        <NavigationIcon
          iconKey={item.iconKey}
          isActive={isActive}
          disabled={isDisabled}
          size={20}
        />
      </span>
      <span
        className={`text-[10px] leading-tight text-center line-clamp-2 max-w-[56px] ${
          isDisabled
            ? 'text-secondary-100'
            : isActive
              ? 'text-primary-900 font-medium'
              : 'text-darkGrey-400'
        }`}
      >
        {shortenLabel(item.name)}
      </span>
      {isActive && (
        <span className="absolute top-0 left-1/2 -translate-x-1/2 w-5 h-0.5 rounded-full bg-primary-900" />
      )}
    </>
  );

  if (isDisabled) {
    return (
      <div className="relative flex flex-col items-center justify-center gap-0.5 min-h-[56px] opacity-50">
        {content}
      </div>
    );
  }

  return (
    <Link href={item.href} onClick={onNavigate}>
      <a className="relative flex flex-col items-center justify-center gap-0.5 min-h-[56px] min-w-[44px] px-1 transition-colors duration-200 active:scale-95">
        {content}
      </a>
    </Link>
  );
}

NavItem.propTypes = {
  item: PropTypes.shape({
    href: PropTypes.string,
    iconKey: PropTypes.string,
    name: PropTypes.string,
    active: PropTypes.bool,
    disabled: PropTypes.bool,
  }).isRequired,
  onNavigate: PropTypes.func,
};

function MoreSheetItem({ item, onNavigate }) {
  const isActive = item.active;
  const isDisabled = item.disabled;

  const inner = (
    <div
      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl transition-colors duration-200 ${
        isActive
          ? 'bg-primary-900/10'
          : isDisabled
            ? 'opacity-50'
            : 'hover:bg-primary-700 active:bg-primary-700'
      }`}
    >
      <NavigationIcon
        iconKey={item.iconKey}
        isActive={isActive}
        disabled={isDisabled}
        size={24}
      />
      <span
        className={`text-[11px] text-center leading-tight line-clamp-2 ${
          isActive ? 'text-primary-900 font-medium' : 'text-darkGrey-400'
        }`}
      >
        {item.name}
      </span>
    </div>
  );

  if (isDisabled) {
    return <div>{inner}</div>;
  }

  return (
    <Link href={item.href} onClick={onNavigate}>
      <a>{inner}</a>
    </Link>
  );
}

MoreSheetItem.propTypes = {
  item: PropTypes.shape({
    href: PropTypes.string,
    iconKey: PropTypes.string,
    name: PropTypes.string,
    active: PropTypes.bool,
    disabled: PropTypes.bool,
  }).isRequired,
  onNavigate: PropTypes.func,
};

const MobileBottomNav = ({ className = '' }) => {
  const router = useRouter();
  const { menuItems, footerItems } = useNavigationMenu();
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  const primaryItems = menuItems.slice(0, MOBILE_PRIMARY_NAV_COUNT);
  const overflowItems = menuItems.slice(MOBILE_PRIMARY_NAV_COUNT);
  const sheetItems = [...overflowItems, ...footerItems];

  const isMoreActive =
    overflowItems.some((item) => item.active) ||
    footerItems.some((item) => item.active) ||
    router.pathname.includes('/loyalty-lookup') ||
    router.pathname.includes('/settings');

  const closeMore = useCallback(() => setIsMoreOpen(false), []);

  useEffect(() => {
    const handleRouteChange = () => setIsMoreOpen(false);
    router.events.on('routeChangeStart', handleRouteChange);
    return () => router.events.off('routeChangeStart', handleRouteChange);
  }, [router.events]);

  useEffect(() => {
    if (!isMoreOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMoreOpen]);

  return (
    <>
      {isMoreOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-[2px] md:hidden"
          onClick={closeMore}
          aria-hidden="true"
        />
      )}

      {isMoreOpen && (
        <div
          className="fixed inset-x-0 bottom-[calc(56px+env(safe-area-inset-bottom))] z-[61] md:hidden w-full"
          role="dialog"
          aria-label="More navigation options"
        >
          <div className="w-full bg-white rounded-t-2xl shadow-2xl border-t border-x border-secondary-200 overflow-hidden animate-[slideUp_0.25s_ease-out]">
            <div className="flex items-center justify-between px-4 py-3 border-b border-secondary-200">
              <span className="text-sm font-semibold text-primary-900">More</span>
              <button
                type="button"
                onClick={closeMore}
                aria-label="Close menu"
                className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-primary-700 transition-colors"
              >
                <IcomoonIcon icon="Close" className="w-4 h-4 fill-darkGrey-400" />
              </button>
            </div>

            <div className="px-4 py-3 border-b border-secondary-200 space-y-3">
              <SearchBox className="w-full h-9" inputClassName="text-sm" />
              <DashboardLoyaltyTabs onNavigate={closeMore} fullWidth />
            </div>

            {sheetItems.length > 0 && (
              <div className="grid grid-cols-3 gap-1 p-3 max-h-[40vh] overflow-y-auto scrollbar">
                {sheetItems.map((item, index) => (
                  <MoreSheetItem key={`${item.name}-${index}`} item={item} onNavigate={closeMore} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <nav
        className={`fixed inset-x-0 bottom-0 z-[62] md:hidden bg-white/95 backdrop-blur-md border-t border-secondary-200 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] ${className}`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        aria-label="Main navigation"
      >
        <div className="flex items-stretch justify-around px-1 pt-1">
          {primaryItems.map((item, index) => (
            <NavItem key={`${item.name}-${index}`} item={item} />
          ))}

          <button
            type="button"
            onClick={() => setIsMoreOpen((open) => !open)}
            aria-expanded={isMoreOpen}
            aria-label="More navigation options"
            className={`relative flex flex-col items-center justify-center gap-0.5 min-h-[56px] min-w-[44px] px-1 transition-all duration-200 active:scale-95 ${
              isMoreOpen || isMoreActive ? 'text-primary-900' : 'text-darkGrey-400'
            }`}
          >
            <span
              className={`flex items-center justify-center w-9 h-9 rounded-xl transition-colors duration-200 ${
                isMoreOpen || isMoreActive ? 'bg-primary-900/10' : ''
              }`}
            >
              <NavigationIcon
                iconKey={NAV_ICON_KEYS.more}
                isActive={isMoreOpen || isMoreActive}
                size={20}
              />
            </span>
            <span
              className={`text-[10px] leading-tight font-medium ${
                isMoreOpen || isMoreActive ? 'text-primary-900' : 'text-darkGrey-400'
              }`}
            >
              More
            </span>
            {(isMoreOpen || isMoreActive) && (
              <span className="absolute top-0 left-1/2 -translate-x-1/2 w-5 h-0.5 rounded-full bg-primary-900" />
            )}
          </button>
        </div>
      </nav>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

MobileBottomNav.propTypes = {
  className: PropTypes.string,
};

export default MobileBottomNav;
